import type { Editor } from "@tiptap/core";

import * as React from "react";

import AssetComponent from "@agent-ui/features/chat/components/chat-elements/message-input/assetNodeExtension";
import { FilePreview } from "@agent-ui/features/chat/components/chat-elements/message-input/FilePreview";
import ProductComponent from "@agent-ui/features/chat/components/chat-elements/message-input/productNodeExtension";
import { VoiceInputButton } from "@agent-ui/features/chat/components/chat-elements/message-input/VoiceInputButton";
import HardBreakMarkdown from "@agent-ui/features/chat/extensions/hardBreakMarkdown";
import useChatMessageLimits from "@agent-ui/features/chat/hooks/useChatMessageLimits";
import { useCurrentChatContext } from "@agent-ui/features/chat/providers/CurrentChatContext";
import { showFileUploadDialog } from "@agent-ui/features/chat/utils/fileUpload";
import {
  chatEditorHandlePaste,
  getCleanedChatPrompt,
} from "@agent-ui/features/chat/utils/tiptapUtils";
import { Button } from "@replo/design-system/components/button/Button";
import { Toggle } from "@replo/design-system/components/toggle/Toggle";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import Placeholder from "@tiptap/extension-placeholder";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { ArrowUp, AtSign, Plus } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { Markdown as TiptapMarkdown } from "tiptap-markdown";

function PageSearchInput({
  onSubmit,
  onTextChange,
  onEditorCreated,
  isContextMenuOpen,
  onContextMenuOpenChange,
  hoveredSuggestion,
  onHoveredSuggestionClear,
}: {
  onSubmit: (prompt: string) => void;
  onTextChange: (text: string) => void;
  onEditorCreated: (editor: Editor) => void;
  isContextMenuOpen: boolean;
  onContextMenuOpenChange: (newValue: boolean) => void;
  hoveredSuggestion: string | null;
  onHoveredSuggestionClear: () => void;
}) {
  const { files, setFiles, addFiles } = useCurrentChatContext();
  const { hasReachedLimit } = useChatMessageLimits();

  const isContextMenuOpenRef = React.useRef(isContextMenuOpen);
  isContextMenuOpenRef.current = isContextMenuOpen;
  const onContextMenuOpenChangeRef = React.useRef(onContextMenuOpenChange);
  onContextMenuOpenChangeRef.current = onContextMenuOpenChange;
  const onTextChangeRef = React.useRef(onTextChange);
  onTextChangeRef.current = onTextChange;
  const hoveredSuggestionRef = React.useRef(hoveredSuggestion);
  hoveredSuggestionRef.current = hoveredSuggestion;
  const hasReachedLimitRef = React.useRef(hasReachedLimit);
  hasReachedLimitRef.current = hasReachedLimit;
  const onHoveredSuggestionClearRef = React.useRef(onHoveredSuggestionClear);
  onHoveredSuggestionClearRef.current = onHoveredSuggestionClear;

  const handleSubmit = () => {
    if (!editor || hasReachedLimitRef.current) {
      return;
    }
    const prompt = getCleanedChatPrompt(editor);
    if (!prompt.trim()) {
      return;
    }
    editor.commands.setContent("", false);
    onSubmit(prompt);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ hardBreak: false }),
      AssetComponent,
      ProductComponent,
      HardBreakMarkdown,
      TiptapMarkdown.configure({
        breaks: false,
        html: true,
        tightLists: true,
      }),
      Placeholder.configure({
        placeholder: "Search pages or describe a new one…",
      }),
    ],
    content: "",
    onUpdate: ({ editor: ed }) => {
      const { state } = ed;
      const { anchor } = state.selection;
      const charBefore =
        anchor > 1 ? state.doc.textBetween(anchor - 1, anchor, "", "") : "";

      // NOTE (Berkley, 2026-03-03): We intentionally avoid chatEditorOnUpdate
      // here because it dispatches to CurrentChatContext which would open the
      // main chat's external context menu popover on top of this modal.
      if (charBefore === "@" && !isContextMenuOpenRef.current) {
        onContextMenuOpenChangeRef.current(true);
      }

      onTextChangeRef.current(ed.getText());
    },
    onCreate: ({ editor: ed }) => {
      ed.commands.focus();
      onEditorCreated(ed);
    },
    editorProps: {
      attributes: {
        style: "font-size: 14px; white-space: pre-wrap;",
        class: "quick-switcher-chat",
      },
      // NOTE (Berkley, 2026-03-03): We use a local handleKeyDown instead of
      // chatEditorHandleKeyDown because the shared helper reads isContextMenuOpen
      // from CurrentChatContext (the main chat), not this modal's own state.
      handleKeyDown: (_view, event) => {
        if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          handleSubmit();
          return true;
        }
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          hoveredSuggestionRef.current
        ) {
          event.preventDefault();
          if (editor) {
            const suggestion = hoveredSuggestionRef.current;
            hoveredSuggestionRef.current = null;
            onHoveredSuggestionClearRef.current();
            editor.commands.setContent(suggestion, false);
            editor.commands.focus("end");
            onTextChangeRef.current(editor.getText());
          }
          return true;
        }
        if (
          event.key === "Enter" &&
          !event.shiftKey &&
          !isContextMenuOpenRef.current
        ) {
          event.preventDefault();
          handleSubmit();
          return true;
        }
        return false;
      },
      handlePaste: chatEditorHandlePaste(addFiles),
    },
  });

  return (
    // TODO REPL-24748 (Berkley, 2026-03-04): Decouple CurrentChatContext from MessageInput so it can be reused here
    <div className="flex flex-col">
      <style>
        {`
          .quick-switcher-chat.tiptap p.is-editor-empty:first-child::before {
            color: var(--color-neutral-400);
            content: attr(data-placeholder);
            float: left;
            height: 0;
            pointer-events: none;
          }
        `}
      </style>
      <div
        className="px-4 pt-4 pb-3 cursor-text"
        onClick={() => editor?.commands.focus()}
      >
        {files && files.length > 0 && (
          <div className="overflow-x-auto pb-2">
            <div className="flex space-x-3">
              <AnimatePresence mode="popLayout">
                {files.map((file) => (
                  <FilePreview
                    key={file.file.name + String(file.file.lastModified)}
                    file={file}
                    onRemove={() => {
                      setFiles((current) => current.filter((f) => f !== file));
                    }}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}

        <EditorContent
          className="w-full min-h-[28px] max-h-[100px] overflow-y-auto"
          editor={editor}
        />

        <div className="flex flex-row justify-between gap-2 w-full pt-3">
          <div className="flex gap-2">
            <Toggle
              icon
              size="base"
              pressed={isContextMenuOpen}
              onPressedChange={(pressed) => {
                onContextMenuOpenChange(pressed);
              }}
            >
              <AtSign />
            </Toggle>
            <Button
              icon
              variant="secondary"
              size="base"
              onClick={(e) => {
                e.stopPropagation();
                void showFileUploadDialog().then((files) => {
                  void addFiles(files);
                });
              }}
            >
              <Plus />
            </Button>
          </div>
          <div className="flex gap-2">
            <VoiceInputButton ref={null} editor={editor} />
            <Tooltip
              content={
                hasReachedLimit ? "You've used all your messages" : "Send to AI"
              }
              triggerAsChild
            >
              <Button
                icon
                variant="primary"
                size="base"
                disabled={hasReachedLimit}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSubmit();
                }}
              >
                <ArrowUp />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PageSearchInput;
